import { useState, useRef, useEffect, useCallback } from 'react';
import { isAllowedFile, isImageType, MAX_ATTACHMENT_SIZE } from '@/services/supportChat';

const SIZE_ERROR = '파일 크기가 2GB를 초과합니다.';
const TYPE_ERROR = '보안상 전송할 수 없는 파일 형식입니다.';
// Files are sent one message each. 10 fits the direct-upload limits; the
// legacy fallback (R2 down) allows 6/min, so a larger batch stops with a
// "try again shortly" message and keeps the rest queued.
export const MAX_PENDING_FILES = 10;
const COUNT_ERROR = `한 번에 최대 ${MAX_PENDING_FILES}개까지 보낼 수 있습니다.`;

export interface PendingFile {
  id: number;
  file: File;
  /** Object URL for image thumbnails, null for other files. */
  preview: string | null;
}

export interface UseFileAttachmentOptions {
  /** Ignore drag-and-drop while true (e.g. a message is sending). */
  disabled?: boolean;
}

function dragCarriesFiles(e: React.DragEvent): boolean {
  return Array.from(e.dataTransfer?.types ?? []).includes('Files');
}

function revoke(items: PendingFile[]) {
  items.forEach((item) => {
    if (item.preview) URL.revokeObjectURL(item.preview);
  });
}

export function useFileAttachment(options?: UseFileAttachmentOptions) {
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const nextIdRef = useRef(1);
  // Counts nested dragenter/dragleave pairs so the overlay doesn't flicker
  // when the cursor crosses a child element's border.
  const dragDepthRef = useRef(0);
  const isBusy = uploading || Boolean(options?.disabled);

  // Cleanup preview URLs on unmount
  const pendingFilesRef = useRef(pendingFiles);
  pendingFilesRef.current = pendingFiles;
  useEffect(() => () => revoke(pendingFilesRef.current), []);

  const toPending = useCallback((file: File): PendingFile => ({
    id: nextIdRef.current++,
    file,
    preview: isImageType(file.type) ? URL.createObjectURL(file) : null,
  }), []);

  const clearPendingFiles = useCallback(() => {
    revoke(pendingFilesRef.current);
    setPendingFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const removePendingFile = useCallback((id: number) => {
    revoke(pendingFilesRef.current.filter((item) => item.id === id));
    setPendingFiles((prev) => prev.filter((item) => item.id !== id));
  }, []);

  /** Validates and queues files; rejected ones are reported in one alert. */
  const addFiles = useCallback((files: File[]) => {
    if (files.length === 0) return;
    const accepted: File[] = [];
    const problems = new Set<string>();
    files.forEach((file) => {
      if (isAllowedFile(file)) accepted.push(file);
      else problems.add(file.size > MAX_ATTACHMENT_SIZE ? SIZE_ERROR : TYPE_ERROR);
    });
    const room = MAX_PENDING_FILES - pendingFilesRef.current.length;
    if (accepted.length > room) problems.add(COUNT_ERROR);
    const queued = accepted.slice(0, Math.max(room, 0)).map(toPending);
    if (queued.length > 0) setPendingFiles((prev) => [...prev, ...queued]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (problems.size > 0) alert(Array.from(problems).join('\n'));
  }, [toPending]);

  /** Puts files back at the front of the queue after a failed send. */
  const restorePendingFiles = useCallback((files: File[]) => {
    if (files.length === 0) return;
    const combined = [...files.map(toPending), ...pendingFilesRef.current];
    const dropped = combined.slice(MAX_PENDING_FILES);
    revoke(dropped);
    setPendingFiles(combined.slice(0, MAX_PENDING_FILES));
    if (dropped.length > 0) alert(`${COUNT_ERROR} 넘친 ${dropped.length}개는 다시 첨부해 주세요.`);
  }, [toPending]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    // Same as drag-and-drop: no attaching while a batch is sending.
    if (!items || isBusy) return;
    const images: File[] = [];
    Array.from(items).forEach((item, index) => {
      if (!item.type.startsWith('image/')) return;
      const file = item.getAsFile();
      if (!file) return;
      const ext = file.type.split('/')[1] || 'png';
      const name = file.name && file.name !== 'image.png' ? file.name : `clipboard-${Date.now()}-${index + 1}.${ext}`;
      images.push(new File([file], name, { type: file.type }));
    });
    if (images.length === 0) return;
    e.preventDefault();
    addFiles(images);
  }, [addFiles, isBusy]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    if (isBusy || !dragCarriesFiles(e)) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setIsDragging(true);
  }, [isBusy]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (isBusy || !dragCarriesFiles(e)) return;
    // Required so the browser drop handler fires instead of opening the file.
    e.preventDefault();
  }, [isBusy]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (isBusy || !dragCarriesFiles(e)) return;
    e.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragging(false);
  }, [isBusy]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    if (!dragCarriesFiles(e)) return;
    e.preventDefault();
    dragDepthRef.current = 0;
    setIsDragging(false);
    if (isBusy) return;
    addFiles(Array.from(e.dataTransfer.files ?? []));
  }, [isBusy, addFiles]);

  return {
    pendingFiles,
    uploading,
    setUploading,
    isDragging,
    fileInputRef,
    addFiles,
    removePendingFile,
    clearPendingFiles,
    restorePendingFiles,
    handlePaste,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  };
}
